import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { envConfig } from "../../../utils/config";
import { useAuth } from "../../modules/auth";
import { AuthModel } from "../../modules/auth/core/_models";
import { AppDispatch } from "../../../redux/store";
import { clearReduxAction } from "../../../redux/actions/auth.actions";
import { useQueryClient } from '@tanstack/react-query';


declare global {
    interface Window {
        ChatBotWidget?: {
            init: (config: {
                loadBootstrap: boolean;
                loadCookieUtils: boolean;
            }) => void;
        };

        CHATBOT_INITIALIZED?: boolean;
    }
}

const setWidgetVisible = (visible: boolean) => {
    const widgetRoot = document.getElementById("chatbot-widget-root");

    if (widgetRoot) {
        widgetRoot.style.display = visible ? "block" : "none";
    }
};

/**
 * Loads the EVE widget. The widget applies the user's EVE rights itself: everyone can
 * sign in through it (EVE and the platform share one login), and a role without EVE
 * View is told so in the panel, after which its EVE button is hidden.
 */
const ChatbotWidget = () => {
    const { saveAuth, logout } = useAuth();
    const dispatch = useDispatch<AppDispatch>();
    const queryClient = useQueryClient();
    useEffect(() => {
        const CHATBOT_WIDGET_VERSION =
            envConfig.APP_CHATBOT_WIDGET_VERSION;

        // Prevent duplicate script load
        const existingScript = document.querySelector(
            'script[src*="chatbot-widget.umd.js"]'
        ) as HTMLScriptElement | null;

        let script: HTMLScriptElement | null = null;

        const showWidget = () => setWidgetVisible(true);

        const initializeWidget = () => {
            // Missing Initialization Guard
            if (!window.CHATBOT_INITIALIZED) {
                window.ChatBotWidget?.init({
                    loadBootstrap: false,
                    loadCookieUtils: false,
                });

                window.CHATBOT_INITIALIZED = true;

                // wait for widget DOM creation
                setTimeout(showWidget, 100);
            } else {
                showWidget();
            }
        };

        if (!existingScript) {
            script = document.createElement("script");

            script.src = `${envConfig.APP_MEDIA_URL}/js/chatbot-widget.umd.js?v=${CHATBOT_WIDGET_VERSION}`;
            script.async = true;

            script.onload = () => {
                initializeWidget();
            };

            document.body.appendChild(script);
        } else {
            initializeWidget();
        }

        const handleLogin = (e: Event) => {
            const customEvent = e as CustomEvent<{
                loginData?: AuthModel;
            }>;

            const loginData = customEvent.detail?.loginData;
            if (!loginData?.user_id) {
                console.error("Chatbot login event missing valid loginData");
                return;
            }

            dispatch(clearReduxAction());
            saveAuth(loginData);
        };

        const handleLogout = () => {
            queryClient.clear(); // Clear all cached data
            logout({ skipReturnUrl: true });
        };

        window.addEventListener(
            "eve-chatbot-widget-login-success",
            handleLogin as EventListener
        );

        window.addEventListener(
            "eve-chatbot-widget-logout-success",
            handleLogout
        );

        // Cleanup
        return () => {
            setWidgetVisible(false);

            window.removeEventListener(
                "eve-chatbot-widget-login-success",
                handleLogin as EventListener
            );

            window.removeEventListener(
                "eve-chatbot-widget-logout-success",
                handleLogout
            );

            window.CHATBOT_INITIALIZED = false;
        };
    }, [saveAuth, logout, dispatch]);

    return null;
};

export default ChatbotWidget;
